/**
 * Painel de Chat com IA sobre a transcrição.
 */

import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react'
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  Tooltip,
  Snackbar,
  useTheme,
} from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import PersonIcon from '@mui/icons-material/Person'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import CheckIcon from '@mui/icons-material/Check'
import { chatWithTranscription, ChatMessageItem, ChatResponseData } from '../../../services/api'

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
}

type Props = {
  fileId: string
  onTimestampClick?: (seconds: number) => void
}

const SUGGESTIONS = [
  'Resuma a transcrição em tópicos',
  'Quais os principais pontos discutidos?',
  'Quem são os participantes e seus papéis?',
  'Identifique questões jurídicas mencionadas',
  'Houve algum ponto de discordância?',
]

function parseTimestamp(ts: string): number | null {
  const parts = ts.split(':').map(Number)
  if (parts.some(isNaN)) return null
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2]
  if (parts.length === 2) return parts[0] * 60 + parts[1]
  return null
}

/** Renderiza inline markdown: **bold**, *italic*, `code`, [timestamp] */
function renderInline(text: string, onTimestampClick?: (s: number) => void): React.ReactNode[] {
  const regex = /\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`|\[(\d{1,2}:\d{2}(?::\d{2})?)\]/g
  const parts: React.ReactNode[] = []
  let last = 0, idx = 0
  let m: RegExpExecArray | null
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push(<React.Fragment key={idx++}>{text.slice(last, m.index)}</React.Fragment>)
    if (m[1] !== undefined) parts.push(<strong key={idx++}>{m[1]}</strong>)
    else if (m[2] !== undefined) parts.push(<em key={idx++}>{m[2]}</em>)
    else if (m[3] !== undefined) parts.push(
      <Box key={idx++} component="code" sx={{ fontFamily: 'monospace', bgcolor: 'rgba(0,0,0,0.07)', px: 0.5, borderRadius: 0.5, fontSize: '0.75em' }}>{m[3]}</Box>
    )
    else if (m[4] !== undefined) {
      const ts = m[4], seconds = parseTimestamp(ts)
      if (seconds !== null && onTimestampClick) {
        parts.push(
          <Chip key={idx++} label={ts} size="small" variant="outlined" onClick={() => onTimestampClick(seconds)}
            sx={{ cursor: 'pointer', mx: 0.3, height: 20, fontSize: '0.68rem', fontFamily: 'monospace', '&:hover': { bgcolor: 'primary.main', color: '#fff' } }} />
        )
      } else parts.push(<React.Fragment key={idx++}>[{ts}]</React.Fragment>)
    }
    last = regex.lastIndex
  }
  if (last < text.length) parts.push(<React.Fragment key={idx++}>{text.slice(last)}</React.Fragment>)
  return parts
}

/**
 * Renderiza markdown completo.
 * Listas são renderizadas como divs para evitar reset de counter pelo WordPress.
 */
function renderMarkdown(text: string, compact = true, onTimestampClick?: (s: number) => void): React.ReactNode {
  const fontSize = compact ? '0.8rem' : '0.875rem'
  const lines = text.split('\n')
  const result: React.ReactNode[] = []
  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    if (line.trim() === '') { result.push(<Box key={`g${i}`} sx={{ height: compact ? '0.3rem' : '0.5rem' }} />); i++; continue }
    const hMatch = line.match(/^(#{1,3})\s+(.+)$/)
    if (hMatch) {
      result.push(
        <Box key={i} sx={{ fontWeight: 700, fontSize: hMatch[1].length === 1 ? (compact ? '0.88rem' : '1rem') : fontSize, lineHeight: 1.4, mt: 0.5, mb: 0.1 }}>
          {renderInline(hMatch[2], onTimestampClick)}
        </Box>
      )
      i++; continue
    }
    if (/^[-*+]\s/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^[-*+]\s/.test(lines[i])) { items.push(lines[i].replace(/^[-*+]\s+/, '')); i++ }
      result.push(
        <Box key={`ul${i}`} sx={{ my: 0.25 }}>
          {items.map((item, j) => (
            <Box key={j} sx={{ display: 'flex', gap: 0.75, lineHeight: 1.5, alignItems: 'flex-start' }}>
              <Box component="span" sx={{ mt: '3px', width: 6, height: 6, borderRadius: '50%', bgcolor: 'text.secondary', flexShrink: 0 }} />
              <Box sx={{ flex: 1 }}>{renderInline(item, onTimestampClick)}</Box>
            </Box>
          ))}
        </Box>
      )
      continue
    }
    if (/^\d+\.\s/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) { items.push(lines[i].replace(/^\d+\.\s+/, '')); i++ }
      result.push(
        <Box key={`ol${i}`} sx={{ my: 0.25 }}>
          {items.map((item, j) => (
            <Box key={j} sx={{ display: 'flex', gap: 0.75, lineHeight: 1.5 }}>
              <Box component="span" sx={{ fontWeight: 600, minWidth: 18, flexShrink: 0, color: 'text.secondary' }}>{j + 1}.</Box>
              <Box sx={{ flex: 1 }}>{renderInline(item, onTimestampClick)}</Box>
            </Box>
          ))}
        </Box>
      )
      continue
    }
    result.push(<Box key={i} sx={{ lineHeight: 1.5 }}>{renderInline(line, onTimestampClick)}</Box>)
    i++
  }
  return <Box sx={{ fontSize, wordBreak: 'break-word' }}>{result}</Box>
}

/** Formata o histórico do chat como texto puro para cópia */
function formatChatAsText(messages: ChatMessage[]): string {
  const header = `=== Chat IA — Análise de Transcrição ===\nExportado em: ${new Date().toLocaleString('pt-BR')}\n\n`
  return header + messages.map(m =>
    `[${m.role === 'user' ? 'Você' : 'IA'}]\n${m.content}`
  ).join('\n\n---\n\n')
}

/** Abre janela de impressão/PDF com o histórico formatado em HTML */
function exportChatAsPDF(messages: ChatMessage[]) {
  const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  const rows = messages.map(m => {
    const isUser = m.role === 'user'
    const lines = escapeHtml(m.content).replace(/\n/g, '<br>')
    const time = m.timestamp.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    return `
      <div class="msg ${isUser ? 'user' : 'ai'}">
        <div class="label">${isUser ? '👤 Você' : '🤖 IA'} <span class="time">${time}</span></div>
        <div class="body">${lines}</div>
      </div>`
  }).join('')

  const win = window.open('', '_blank', 'width=800,height=700')
  if (!win) return
  win.document.write(`<!DOCTYPE html><html lang="pt-BR"><head>
    <meta charset="UTF-8">
    <title>Chat IA — ${new Date().toLocaleDateString('pt-BR')}</title>
    <style>
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 13px; color: #222; padding: 32px; background: #fff; }
      h1 { font-size: 16px; color: #1976d2; border-bottom: 2px solid #1976d2; pb: 8px; margin-bottom: 24px; padding-bottom: 10px; }
      .meta { font-size: 11px; color: #888; margin-bottom: 24px; }
      .msg { margin-bottom: 16px; max-width: 90%; }
      .msg.user { margin-left: auto; }
      .label { font-size: 11px; font-weight: 600; color: #666; margin-bottom: 4px; }
      .time { font-weight: 400; color: #aaa; margin-left: 6px; }
      .body { padding: 10px 14px; border-radius: 10px; line-height: 1.6; white-space: pre-wrap; }
      .user .body { background: #1976d2; color: #fff; border-radius: 12px 12px 2px 12px; }
      .ai .body { background: #f5f5f5; border-radius: 12px 12px 12px 2px; }
      @media print { body { padding: 16px; } }
    </style>
  </head><body>
    <h1>🤖 Chat IA — Análise de Transcrição</h1>
    <p class="meta">Exportado em ${new Date().toLocaleString('pt-BR')}</p>
    ${rows}
  </body></html>`)
  win.document.close()
  setTimeout(() => { win.focus(); win.print() }, 300)
}

// ─── Bubble de mensagem ──────────────────────────────────────────────────────
function MessageBubble({ msg, compact, onTimestampClick, theme }: {
  msg: ChatMessage
  compact: boolean
  onTimestampClick?: (s: number) => void
  theme: any
}) {
  const isUser = msg.role === 'user'
  return (
    <Box sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', gap: 0.75, alignItems: 'flex-end' }}>
      {!isUser && (
        <Box sx={{
          width: compact ? 24 : 28, height: compact ? 24 : 28,
          borderRadius: '50%', bgcolor: '#e3f2fd', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mb: 0.25,
        }}>
          <SmartToyIcon sx={{ fontSize: compact ? 14 : 16, color: '#1976d2' }} />
        </Box>
      )}
      <Paper elevation={0} sx={{
        px: compact ? 1.25 : 1.75, py: compact ? 0.75 : 1,
        maxWidth: '82%',
        borderRadius: isUser ? '14px 14px 2px 14px' : '14px 14px 14px 2px',
        bgcolor: isUser ? '#5b8def' : (theme.palette.mode === 'dark' ? '#2a2a2a' : '#f0f4f8'),
        color: isUser ? '#fff' : 'text.primary',
        boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
        '#monkee-file-transcript-client &': {
          backgroundColor: isUser ? '#5b8def !important' : '#f0f4f8 !important',
          color: isUser ? '#fff !important' : '#222 !important',
        },
      }}>
        {isUser ? (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: compact ? '0.8rem' : '0.875rem', lineHeight: 1.55, color: '#fff', '#monkee-file-transcript-client &': { color: '#fff !important' } }}>
            {msg.content}
          </Typography>
        ) : (
          renderMarkdown(msg.content, compact, onTimestampClick)
        )}
      </Paper>
      {isUser && (
        <Box sx={{
          width: compact ? 24 : 28, height: compact ? 24 : 28,
          borderRadius: '50%', bgcolor: '#e8e8e8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mb: 0.25,
        }}>
          <PersonIcon sx={{ fontSize: compact ? 14 : 16, color: '#666' }} />
        </Box>
      )}
    </Box>
  )
}

// ─── Barra de ações ──────────────────────────────────────────────────────────
function ChatActions({ hasMessages, copied, onCopy, onPdf, onClear, onExpand, compact }: {
  hasMessages: boolean
  copied: boolean
  onCopy: () => void
  onPdf: () => void
  onClear: () => void
  onExpand: () => void
  compact?: boolean
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.25, px: 0.75, py: 0.5 }}>
      {hasMessages && (
        <>
          <Tooltip title={copied ? 'Copiado!' : 'Copiar conversa'} placement="bottom">
            <IconButton size="small" onClick={onCopy} sx={{ color: copied ? '#4caf50' : 'text.secondary', transition: 'color 0.2s' }}>
              {copied ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Exportar como PDF" placement="bottom">
            <IconButton size="small" onClick={onPdf} sx={{ color: 'text.secondary' }}>
              <PictureAsPdfIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Box sx={{ width: '1px', height: 16, bgcolor: '#e0e0e0', mx: 0.5, flexShrink: 0 }} />
          <Tooltip title="Limpar conversa" placement="bottom">
            <IconButton size="small" onClick={onClear} sx={{ color: 'text.secondary', '&:hover': { color: '#d32f2f' } }}>
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </>
      )}
      <Tooltip title="Expandir chat" placement="bottom">
        <IconButton size="small" onClick={onExpand} sx={{ color: 'text.secondary' }}>
          <OpenInFullIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

// ─── Input de digitação ──────────────────────────────────────────────────────
function ChatInput({ inputRef, value, onChange, onKeyDown, onSend, disabled, autoFocus, compact }: {
  inputRef?: React.RefObject<HTMLInputElement>
  value: string
  onChange: (v: string) => void
  onKeyDown: (e: React.KeyboardEvent) => void
  onSend: () => void
  disabled: boolean
  autoFocus?: boolean
  compact?: boolean
}) {
  const containerRef = React.useRef<HTMLDivElement>(null)

  const handleFocus = () => {
    if (containerRef.current) {
      containerRef.current.style.borderColor = '#1976d2'
    }
  }

  const handleBlur = () => {
    if (containerRef.current) {
      containerRef.current.style.borderColor = '#e0e0e0'
    }
  }

  return (
    <Box
      ref={containerRef}
      onFocus={handleFocus}
      onBlur={handleBlur}
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 4,
        border: '1.5px solid #e0e0e0',
        borderRadius: 12,
        padding: '4px 10px',
        backgroundColor: '#fff',
        transition: 'border-color 0.2s',
        boxSizing: 'border-box',
      }}
    >
      <TextField
        inputRef={inputRef}
        fullWidth
        multiline
        maxRows={4}
        variant="standard"
        placeholder="Pergunte sobre a transcrição..."
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={disabled}
        autoFocus={autoFocus}
        inputProps={{
          style: {
            outline: 'none',
            boxShadow: 'none',
            color: '#333',
            fontSize: compact ? '0.8rem' : '0.875rem',
            lineHeight: 1.5,
            padding: 0,
            WebkitTextFillColor: '#333',
          },
        }}
        sx={{
          '& .MuiInputBase-root': {
            fontSize: compact ? '0.8rem' : '0.875rem',
            lineHeight: 1.5,
            py: 0.5,
            '&::before, &::after': { display: 'none' },
          },
          '& .MuiInputBase-input': { p: 0 },
          '#monkee-file-transcript-client &': {
            '& .MuiInputBase-root::before, & .MuiInputBase-root::after': { display: 'none !important' },
            '& ::placeholder': { color: '#aaa !important', opacity: '1 !important' },
          },
        }}
      />
      <Tooltip title={!value.trim() ? '' : 'Enviar (Enter)'}>
        <span>
          <IconButton
            size="small"
            color="primary"
            onClick={onSend}
            disabled={!value.trim() || disabled}
            sx={{
              mb: 0.25,
              bgcolor: value.trim() && !disabled ? '#1976d2' : 'transparent',
              color: value.trim() && !disabled ? '#fff' : '#bbb',
              width: 30,
              height: 30,
              transition: 'all 0.2s',
              '&:hover': { bgcolor: '#1565c0', color: '#fff' },
              '&.Mui-disabled': { bgcolor: 'transparent', color: '#ccc' },
              '#monkee-file-transcript-client &': {
                backgroundColor: value.trim() && !disabled ? '#1976d2 !important' : 'transparent !important',
                color: value.trim() && !disabled ? '#fff !important' : '#bbb !important',
              },
            }}
          >
            <SendIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  )
}

// ─── Componente principal ────────────────────────────────────────────────────
export default function AIChatPanel({ fileId, onTimestampClick }: Props) {
  const theme = useTheme()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [copied, setCopied] = useState(false)

  // Refs para scroll e layout
  const containerRef = useRef<HTMLDivElement>(null)
  const actionsBarRef = useRef<HTMLDivElement>(null)
  const messagesBoxRef = useRef<HTMLDivElement>(null)
  const inputAreaRef = useRef<HTMLDivElement>(null)
  const panelEndRef = useRef<HTMLDivElement>(null)
  const dialogEndRef = useRef<HTMLDivElement>(null)
  const panelInputRef = useRef<HTMLInputElement>(null)

  /**
   * Ajusta a altura do box de mensagens via inline style (imune a CSS externo).
   * Isso garante scroll correto mesmo quando WordPress override CSS do flex chain.
   */
  const updateLayout = useCallback(() => {
    const container = containerRef.current
    const messagesBox = messagesBoxRef.current
    const actionsBar = actionsBarRef.current
    const inputArea = inputAreaRef.current
    if (!container || !messagesBox) return

    const containerH = container.offsetHeight
    const actionsH = actionsBar?.offsetHeight ?? 0
    const inputH = inputArea?.offsetHeight ?? 54
    const available = containerH - actionsH - inputH

    if (available > 60) {
      messagesBox.style.height = `${available}px`
      messagesBox.style.overflowY = 'scroll'
      messagesBox.style.overflowX = 'hidden'
    }
  }, [])

  useLayoutEffect(() => {
    updateLayout()
    const obs = new ResizeObserver(updateLayout)
    if (containerRef.current) obs.observe(containerRef.current)
    if (inputAreaRef.current) obs.observe(inputAreaRef.current)
    if (actionsBarRef.current) obs.observe(actionsBarRef.current)
    return () => obs.disconnect()
  }, [updateLayout])

  const scrollToBottom = useCallback(() => {
    panelEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    dialogEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => { scrollToBottom() }, [messages, scrollToBottom])

  const buildHistory = useCallback((): ChatMessageItem[] =>
    messages.map(m => ({ role: m.role, content: m.content }))
  , [messages])

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || loading) return
    setMessages(prev => [...prev, { role: 'user', content: text.trim(), timestamp: new Date() }])
    setInput('')
    setError(null)
    setLoading(true)
    try {
      const response: ChatResponseData = await chatWithTranscription(fileId, text.trim(), buildHistory())
      setMessages(prev => [...prev, { role: 'assistant', content: response.message, timestamp: new Date() }])
    } catch (err: any) {
      setError(err?.message || 'Erro ao enviar mensagem. Tente novamente.')
    } finally {
      setLoading(false)
      if (!isFullscreen) panelInputRef.current?.focus()
    }
  }, [fileId, loading, buildHistory, isFullscreen])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input) }
  }, [input, sendMessage])

  const handleClearChat = useCallback(() => { setMessages([]); setError(null) }, [])

  const handleCopy = useCallback(() => {
    if (!messages.length) return
    navigator.clipboard.writeText(formatChatAsText(messages)).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }, [messages])

  const handlePDF = useCallback(() => {
    if (messages.length) exportChatAsPDF(messages)
  }, [messages])

  const showWelcome = messages.length === 0 && !loading

  // Conteúdo de mensagens (reutilizado em painel e fullscreen)
  const renderMessages = (endRef: React.RefObject<HTMLDivElement>, compact: boolean) => (
    <>
      {showWelcome && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, mt: 2, px: 1 }}>
          <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: '#e3f2fd', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SmartToyIcon sx={{ fontSize: 26, color: '#1976d2' }} />
          </Box>
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ fontSize: compact ? '0.78rem' : '0.875rem', lineHeight: 1.5 }}>
            Pergunte qualquer coisa sobre o conteúdo desta transcrição.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
            {SUGGESTIONS.map((s, i) => (
              <Box
                key={i}
                onClick={() => sendMessage(s)}
                sx={{
                  px: 1.25, py: 0.75,
                  borderRadius: 2,
                  border: '1px solid #e0e0e0',
                  cursor: 'pointer',
                  fontSize: compact ? '0.75rem' : '0.8rem',
                  color: '#555',
                  transition: 'all 0.15s',
                  lineHeight: 1.4,
                  '&:hover': { borderColor: '#1976d2', color: '#1976d2', bgcolor: '#f0f7ff' },
                  '#monkee-file-transcript-client &': { backgroundColor: '#fff !important', '&:hover': { backgroundColor: '#f0f7ff !important' } },
                }}
              >
                {s}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {messages.map((msg, i) => (
        <MessageBubble key={i} msg={msg} compact={compact} onTimestampClick={onTimestampClick} theme={theme} />
      ))}

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.75 }}>
          <Box sx={{ width: compact ? 24 : 28, height: compact ? 24 : 28, borderRadius: '50%', bgcolor: '#e3f2fd', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <SmartToyIcon sx={{ fontSize: compact ? 14 : 16, color: '#1976d2' }} />
          </Box>
          <Paper elevation={0} sx={{
            px: 1.5, py: 0.875, borderRadius: '14px 14px 14px 2px',
            bgcolor: '#f0f4f8', display: 'flex', alignItems: 'center', gap: 1,
            '#monkee-file-transcript-client &': { backgroundColor: '#f0f4f8 !important' },
          }}>
            <CircularProgress size={12} thickness={5} />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>Analisando...</Typography>
          </Paper>
        </Box>
      )}

      {error && (
        <Alert severity="error" variant="outlined" sx={{ fontSize: '0.75rem', py: 0.5, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <div ref={endRef} />
    </>
  )

  return (
    <>
      {/* ═══════════════════════════════════════════════════
          PAINEL LATERAL
          Layout com JS-managed height para garantir scroll
          independente do CSS do WordPress
          ═══════════════════════════════════════════════════ */}
      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: '#fff',
          '#monkee-file-transcript-client &': {
            // Altura explícita: viewport - admin bar WP (32px) - header do painel (48px)
            height: 'calc(100vh - 32px - 48px) !important',
            overflow: 'hidden !important',
            backgroundColor: '#fff !important',
          },
        }}
      >
        {/* Barra de ações */}
        <Box
          ref={actionsBarRef}
          sx={{
            borderBottom: '1px solid #f0f0f0',
            flexShrink: 0,
            '#monkee-file-transcript-client &': { borderBottom: '1px solid #f0f0f0 !important' },
          }}
        >
          <ChatActions
            hasMessages={messages.length > 0}
            copied={copied}
            onCopy={handleCopy}
            onPdf={handlePDF}
            onClear={handleClearChat}
            onExpand={() => setIsFullscreen(true)}
            compact
          />
        </Box>

        {/* Área de mensagens — altura controlada por JS via inline style */}
        <Box
          ref={messagesBoxRef}
          sx={{
            px: 1.25,
            py: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.875,
            // Sem height/overflow aqui — gerenciado pelo updateLayout() via inline style
          }}
        >
          {renderMessages(panelEndRef, true)}
        </Box>

        {/* Input */}
        <Box
          ref={inputAreaRef}
          sx={{
            px: 1,
            pb: 1,
            pt: 0.5,
            mt: 'auto',
            flexShrink: 0,
            '#monkee-file-transcript-client &': { backgroundColor: '#fff !important' },
          }}
        >
          <ChatInput
            inputRef={panelInputRef}
            value={input}
            onChange={setInput}
            onKeyDown={handleKeyDown}
            onSend={() => sendMessage(input)}
            disabled={loading}
            compact
          />
          <Typography
            variant="caption"
            sx={{ display: 'block', textAlign: 'center', color: '#bbb', fontSize: '0.6rem', mt: 0.5 }}
          >
            Enter para enviar · Shift+Enter para nova linha
          </Typography>
        </Box>
      </Box>

      {/* ═══════════════════════════════════════════════════
          FULLSCREEN DIALOG
          Renderizado via Portal — sem interferência WP
          ═══════════════════════════════════════════════════ */}
      <Dialog
        fullScreen
        open={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        PaperProps={{ sx: { display: 'flex', flexDirection: 'column', bgcolor: '#f8f9fb' } }}
      >
        {/* Header — visual limpo, alinhado ao painel lateral */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          px: 2, minHeight: 56,
          bgcolor: '#fff',
          flexShrink: 0,
          borderBottom: '1px solid #e8e8e8',
        }}>
          <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: '#e3f2fd', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <SmartToyIcon sx={{ color: '#1976d2', fontSize: 17 }} />
          </Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem', flex: 1, color: 'text.primary' }}>
            Chat IA
          </Typography>
          {messages.length > 0 && (
            <>
              <Tooltip title={copied ? 'Copiado!' : 'Copiar conversa'} placement="bottom">
                <IconButton size="small" onClick={handleCopy} sx={{ color: copied ? '#4caf50' : 'text.secondary', transition: 'color 0.2s' }}>
                  {copied ? <CheckIcon sx={{ fontSize: 18 }} /> : <ContentCopyIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Exportar como PDF" placement="bottom">
                <IconButton size="small" onClick={handlePDF} sx={{ color: 'text.secondary' }}>
                  <PictureAsPdfIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Box sx={{ width: '1px', height: 16, bgcolor: '#e0e0e0', mx: 0.5, flexShrink: 0 }} />
              <Tooltip title="Limpar conversa" placement="bottom">
                <IconButton size="small" onClick={handleClearChat} sx={{ color: 'text.secondary', '&:hover': { color: '#d32f2f' } }}>
                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </>
          )}
          <Box sx={{ width: '1px', height: 16, bgcolor: '#e0e0e0', mx: 0.5, flexShrink: 0 }} />
          <Tooltip title="Fechar" placement="bottom">
            <IconButton size="small" onClick={() => setIsFullscreen(false)} sx={{ color: 'text.secondary' }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Mensagens */}
        <Box sx={{
          flex: 1, minHeight: 0, overflowY: 'auto',
          py: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
          maxWidth: 780, width: '100%', mx: 'auto',
          px: { xs: 2, sm: 3 },
        }}>
          {renderMessages(dialogEndRef, false)}
        </Box>

        {/* Input */}
        <Box sx={{
          flexShrink: 0,
          borderTop: '1px solid #e8e8e8',
          bgcolor: '#fff',
          py: 1.75,
          maxWidth: 780, width: '100%', mx: 'auto',
          px: { xs: 2, sm: 3 },
        }}>
          <ChatInput
            value={input}
            onChange={setInput}
            onKeyDown={handleKeyDown}
            onSend={() => sendMessage(input)}
            disabled={loading}
            autoFocus
            compact={false}
          />
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#bbb', fontSize: '0.7rem', mt: 0.75 }}>
            Enter para enviar · Shift+Enter para nova linha
          </Typography>
        </Box>
      </Dialog>

      {/* Feedback de cópia */}
      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message="Conversa copiada para a área de transferência"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  )
}
